import { z } from 'zod';
import { PROJECT_CODE_SCHEMA } from '../project/project.ts';
import { SUPPORTED_UNITS_SCHEMA } from '../quote-unit/unit-types.ts';
import { BILLING_TYPE_SCHEMA } from '../finance-settings/billing-types.ts';
import {
    ORDER_ORGANIZATION_ROLE_SCHEMA,
    ORDER_ORGANIZATION_TRANSACTIONAL_ROLE_SCHEMA,
} from '../finance-settings/role.ts';
export const PostOrderFulfillUrlSchema = z.object({
    orderId: z.string().uuid(),
});

export const LineItemListingSchema = z.object({
    productId: z.string().uuid(),
    amount: z.number(),
    serviceDate: z.coerce.date(),
    sortIndex: z.number().int(),
    groupKey: z.number().int().optional(),
    referenceDocument: z.string().nullish(),
    licensePlate: z.string().optional(),
    documentUrls: z.array(z.string()).optional(),
    accompanyingDocumentNumber: z.string().optional(),
});

const _FulfillmentLineItemListingSchema = LineItemListingSchema.extend({
    fulfillmentId: z.string().uuid(),
});

export type FulfillmentLineItemListing = z.infer<typeof _FulfillmentLineItemListingSchema>;

export const PaymentOrganizationSchema = z.object({
    organizationId: z.number().int().positive(),
    paymentTermId: z.number().positive().nullable(),
    billingMethod: BILLING_TYPE_SCHEMA,
    // Payment organizations are superseded by finance settings, whose column is varchar(255) and
    // whose response schema declares max(255). Bounding the field here makes 255 the contract
    // everywhere these are accepted, so an over-long text is rejected at a request boundary rather
    // than failing a write further in.
    billingText: z.string().max(255).nullish(),
    transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE_SCHEMA,
    functionalRole: ORDER_ORGANIZATION_ROLE_SCHEMA,
});

export type PaymentOrganization = z.infer<typeof PaymentOrganizationSchema>;

export const PostOrderFulfillNestedProductSchema = z.object({
    id: z.string().uuid(),
    quoteId: z.string().uuid(),
    name: z.string(),
    amount: z.number(),
    unit: SUPPORTED_UNITS_SCHEMA,
    currencyCode: z.string().length(3),
    partnerOrganizationId: z.number().int().positive(),
    orderingOrganizationId: z.number().int().positive(),
    platformOrganizationId: z.number().int().positive(),
    purchasePrice: z.number(),
    purchaseTaxClassId: z.string(),
    salesPrice: z.number(),
    salesTaxClassId: z.string(),
    // Check that service date is in the past
    serviceDate: z.coerce.date().refine(date => new Date() > date),
    //TODO: payment term infos should be mandatory MAR-3417
    paymentOrganizations: z.array(PaymentOrganizationSchema).default([]),
});

export const PostOrderFulfillRequestSchema = z
    .object({
        clientInformation: z.object({
            projectCode: PROJECT_CODE_SCHEMA,
            userId: z.number().int(),
            manualCostCenter: z.string().trim().nonempty().optional(),
        }),
        products: z.array(PostOrderFulfillNestedProductSchema),
        lineItemListing: LineItemListingSchema.array(),
        fulfilledWithIndividualItemListing: z.boolean(),
        supportingDocuments: z
            .array(
                z.object({
                    description: z.string(),
                    urlPath: z.string(),
                    fileName: z.string(),
                })
            )
            .nonempty(),
    })
    .refine(
        data => {
            const lineItems = data.lineItemListing;
            const isGroupKeySet = lineItems.some(item => item.groupKey !== undefined);
            if (isGroupKeySet) {
                return lineItems.every(item => item.groupKey !== undefined);
            }
            return true;
        },
        {
            path: ['lineItemListing'],
            message: 'If groupKey is set for one line item, it must be set for all line items.',
        }
    );

const ContextSchema = z.object({
    userId: z.number(),
    operatingMarket: z.string(),
});

export const PostOrderFulfillInternalRequestSchema = PostOrderFulfillRequestSchema.and(ContextSchema);
